import { useContext, useEffect, useState } from 'react';
import LayersContext from '../../context/LayersContext';
import { formatValue } from '../../utils/helpers';
import ItemCard from './ItemCard';
import searchSvg from '../../assets/search.svg';
import LoadingIndicator from '../LoadingIndicator';
// import { useNavigate } from 'react-router-dom';

interface LayerPanelProps {
    dashboardView: string;
}

const LayerPanel: React.FC<LayerPanelProps> = ({ }) => {
    const { layers, loading } = useContext(LayersContext);
    const [activeCardId, setActiveCardId] = useState<number | null>(null); // Track active card
    const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
    const [showAllCategories, setShowAllCategories] = useState(false);
    const [activeLayer, setActiveLayer] = useState<string>('');
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [searchQuery, setSearchQuery] = useState<string>('');
    const itemsPerPage = 10;

    // Get the layer items from the active layer
    const activeLayerData = layers?.find(layer => layer.layer_name === activeLayer);
    const layerItems = activeLayerData?.items || [];

    // Get summary info for the current layer
    const summary = activeLayerData?.summary?.length ? activeLayerData.summary[0] : null;

    // Update activeLayer when layers become available
    useEffect(() => {
        if (layers.length > 0 && !activeLayer) {
            setActiveLayer(layers[0].layer_name); // Set initial active layer if not set
        }
    }, [layers, activeLayer]);

    // Reset current page to 1 any time the category, search or active layer changes
    useEffect(() => {
        setCurrentPage(1);
    }, [selectedCategories, searchQuery, activeLayer]);

    // Handle active layer change from dropdown
    const handleLayerChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const selectedLayer = layers.find((layer) => layer.layer_name === e.target.value);
        setActiveLayer(selectedLayer?.layer_name || '');
    };

    // Filter layer items based on selected categories
    const filteredLayerItems = selectedCategories.length > 0
    ? layerItems?.filter(item => selectedCategories.includes(item.category))
    : layerItems;

    const searchedLayerItems = filteredLayerItems?.filter(item =>
        item.name.toLowerCase().includes(searchQuery.toLowerCase())
    );

    // Sort the filtered items by activity_date (descending)
    const sortedLayerItems = searchedLayerItems?.sort((a, b) => {
        const dateA = new Date(a.activity_date);
        const dateB = new Date(b.activity_date);
        return dateB.getTime() - dateA.getTime();
    });

    // Calculate the total value of the filtered items
    const totalValue = filteredLayerItems?.reduce((sum, item) => sum + (item.value || 0), 0) || 0;

    // Paginate the filtered items
    const totalItems = filteredLayerItems?.length || 0;
    const totalSearchedItems = sortedLayerItems?.length || 0;
    const totalPages = Math.ceil(totalSearchedItems / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const paginatedItems = sortedLayerItems?.slice(startIndex, startIndex + itemsPerPage);

    // Get unique categories from layerItems for the dropdown
    const categories = Array.from(new Set(layerItems?.map(item => item.category).filter(category => category !== null) || []));

    const visibleCategories = showAllCategories ? categories : categories.slice(0, 5);

    // Handle page change
    const handlePageChange = (page: number) => {
        if (page < 1 || page > totalPages) return; // Prevent invalid page numbers
        setCurrentPage(page);
    };

    // Toggle category selection
    const handleCategoryToggle = (category: string) => {
        setSelectedCategories(prev =>
            prev.includes(category)
                ? prev.filter(c => c !== category) // Remove if already selected
                : [...prev, category] // Add if not selected
        );
    };

    const origin = '*';

    const handleExpandClick = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();

        // set conditions to show the dashboard in a split or normal view
        let splitHorizontal = true;

        // Send message to expand with additional contextual data
        window.parent.postMessage({ 
            message: "expandLeftPanel",
            data: {
                activeLayer,
                splitHorizontal
            }
        }, origin);
        console.log('Expand message sent. Active layer:', activeLayer, "| Split the dashboard horizontally?:", splitHorizontal);

        // Send message to clear map selection
        window.parent.postMessage(
            {
                message: "clearMapSelection",
            },
            origin
        );
        console.log("Clear map selection message sent.");
    }

    if (loading) {
        return <LoadingIndicator />;
    }
    
    // Logic for if there are no items present in the selected layer
    if (layerItems.length === 0 && !activeLayer) {
        return (
            <>
                {/* Header */}
                <div className='flex items-center justify-center bg-[#EFEFEF] h-12'>
                    <select
                        value={activeLayer}
                        onChange={handleLayerChange}
                        className="text-sm font-bold text-port-text bg-transparent outline-none border-none cursor-pointer"
                    >
                        {layers.map((layer) => (
                            <option key={layer.layer_name} value={layer.layer_name}>
                                {layer.layer_name}
                            </option>
                        ))}
                    </select>
                </div>
                <p className='text-xs font-bold text-port-text mt-2 ml-6'>No items present in the current selection.</p>
            </>
        )
    }
    

    return (
        <div>
            {/* Message when no active layer is selected */}
            {(!activeLayer && layers.length> 0) ? (
                <div className="mt-4 p-4 text-center text-gray-600">
                    <p>No active layer selected.</p>
                </div>
            ) : (
                <>
                    {/* Header */}
                    <div className='flex items-center justify-center bg-[#EFEFEF] h-12'>
                        <select
                            value={activeLayer}
                            onChange={handleLayerChange}
                            className="text-sm font-bold text-port-text bg-transparent outline-none border-none cursor-pointer"
                        >
                            {layers.map((layer) => (
                                <option key={layer.layer_name} value={layer.layer_name}>
                                    {layer.layer_name}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="grid grid-cols-3 p-6 pt-0 w-full">
                        {/* Text for no categories or items present */}
                        {layerItems.length === 0 &&
                        <>
                            <p className='col-span-3 mt-4 m-auto text-xs text-port-text'>There are no items from <strong>{activeLayer}</strong> within your current selection.</p>
                            <p className='col-span-3 mt-2 m-auto text-xs text-port-text'>The map is still fully interactive—try widening, moving, or clearing your selection to uncover nearby items.</p>
                        </>
                        }
                        {/* Category Buttons */}
                        {categories.length > 0 &&
                        <div className="mt-4 flex flex-col justify-center col-span-2">
                            {visibleCategories.length > 0 && visibleCategories.map((category, index) => (
                                <button
                                    key={index}
                                    onClick={() => handleCategoryToggle(category)}
                                    className={`flex items-center gap-2 pr-3 mr-8 py-2 border-b text-left ${index === categories.length - 1 ? 'border-b-0' : ''}`}
                                >
                                    <span
                                        className={`w-4 h-4 shrink-0 flex items-center justify-center border border-port-green rounded ${selectedCategories.includes(category) ? "bg-port-green text-white" : "bg-white"
                                        }`}
                                    >
                                        {selectedCategories.includes(category) && <span>✔</span>}
                                    </span>
                                    <p className='text-xs font-bold text-port-text'>{category}</p>
                                </button>
                            ))}

                            {categories.length > 5 && (
                                <button onClick={() => setShowAllCategories(!showAllCategories)} className="text-blue-500 mt-1 text-[10px] text-start ml-6">
                                    {showAllCategories ? "Show Less" : "Show More"}
                                </button>
                            )}
                        </div>
                        }

                        {/* Totals */}
                        <div className="flex flex-col items-start justify-center col-span-1">
                            <div className={`flex flex-col items-start justify-center ${totalItems ? 'visible' : 'invisible'}`}>
                                <p className="text-[44px] font-bold text-port-blue font-barlow-condensed">
                                    {formatValue(totalItems, 'integer')}
                                </p>
                                <p className='text-xs font-bold text-port-text -mt-2'>Total Items</p>
                            </div>

                            <div className={`flex flex-col items-start justify-center ${totalValue ? 'visible' : 'invisible'}`}>
                                <p className="text-[44px] font-bold text-port-blue font-barlow-condensed">
                                    {formatValue(totalValue, "currency")}
                                </p>
                                <p className='text-xs font-bold text-port-text -mt-2'>Total</p>
                                <p className='text-xs text-port-text'>{summary?.unit_descriptor}</p>
                            </div>
                        </div>

                        {/* Program Tour (storymap) */}
                        {layerItems.length > 0 ?
                        <div className='mt-4 flex justify-start col-span-2'>
                            <button onClick={handleExpandClick} className='border border-port-green bg-port-green h-10 px-2 py-1 rounded text-white text-[11px] font-bold'>
                                Start Tour of {activeLayer}
                            </button>
                        </div> : null }

                        {/* Search Bar */}
                        {layerItems.length > 0 &&
                            <div className="col-span-3 flex items-center mt-4 border rounded-lg p-2 bg-white shadow-lg">
                                <input
                                    type="text"
                                    placeholder="Search for sites..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="w-full outline-none px-2 text-xs text-port-text placeholder:text-port-green font-bold"
                                />
                                {searchQuery ? (
                                    <button
                                        onClick={() => setSearchQuery('')}
                                        className="mx-1 text-gray-500 hover:text-port-green text-xs font-bold"
                                    >
                                        ✕
                                    </button>
                                ) : (
                                    <img className="w-4 h-4" src={searchSvg} alt='Search icon'/>
                                )}
                            </div>
                        }
                        

                        {/* Item Cards */}
                        <div className="grid grid-cols-1 gap-4 mt-4 col-span-3">
                            {paginatedItems?.map((layerItem, index) => (
                                <ItemCard 
                                    key={index} 
                                    width="w-full" 
                                    layerItem={layerItem} 
                                    summary={summary}
                                    isActive={activeCardId === layerItem.id}
                                    setActiveCardId={setActiveCardId} 
                                />
                            ))}
                        </div>

                        {/* Pagination Controls */}
                        {totalPages > 0 && (
                            <div className="mt-4 flex justify-center gap-4 text-xs items-center col-span-3">
                                <button
                                    onClick={() => handlePageChange(currentPage - 1)}
                                    disabled={currentPage === 1}
                                    className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md disabled:opacity-50 w-24"
                                >
                                    Previous
                                </button>

                                <span className="text-gray-700">
                                    Page {currentPage} of {totalPages}
                                </span>

                                <button
                                    onClick={() => handlePageChange(currentPage + 1)}
                                    disabled={currentPage === totalPages}
                                    className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md disabled:opacity-50 w-24"
                                >
                                    Next
                                </button>
                            </div>
                        )}
                    </div>
                </>
            )}
        </div>
    );
};

export default LayerPanel;